"use client";
import { cn } from "../../lib/utils";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { createNoise3D } from "simplex-noise";

export const WavyBackground = ({
  children,
  className,
  containerClassName,
  colors,
  waveWidth,
  backgroundFill,
  blur = 10,
  speed = "fast",
  waveOpacity = 0.5,
  ...props
}) => {
  const noise = createNoise3D();
  const canvasRef = useRef(null);
  const animationFrameRef = useRef();
  const contextRef = useRef({
    w: 0,
    h: 0,
    nt: 0,
    i: 0,
    x: 0,
    ctx: null,
    canvas: null
  });

  const getSpeed = useCallback(() => {
    switch (speed) {
      case "slow":
        return 0.001;
      case "fast":
        return 0.002;
      default:
        return 0.001;
    }
  }, [speed]);

  const drawWave = useCallback((n) => {
    const { ctx, w, h } = contextRef.current;
    contextRef.current.nt += getSpeed();
    
    for (let i = 0; i < n; i++) {
      ctx.beginPath();
      ctx.lineWidth = waveWidth || 50;
      ctx.strokeStyle = (colors ?? [
        "#38bdf8",
        "#818cf8",
        "#c084fc",
        "#e879f9",
        "#22d3ee",
      ])[i % (colors?.length ?? 5)];
      
      for (let x = 0; x < w; x += 5) {
        const y = noise(x / 800, 0.3 * i, contextRef.current.nt) * 100;
        ctx.lineTo(x, y + h * 0.5);
      }
      ctx.stroke();
      ctx.closePath();
    }
  }, [colors, noise, waveWidth, getSpeed]);

  const render = useCallback(() => {
    const { ctx, w, h } = contextRef.current;
    ctx.fillStyle = backgroundFill || "black";
    ctx.globalAlpha = waveOpacity || 0.5;
    ctx.fillRect(0, 0, w, h);
    drawWave(5);
    animationFrameRef.current = requestAnimationFrame(render);
  }, [backgroundFill, drawWave, waveOpacity]);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    contextRef.current = {
      ...contextRef.current,
      canvas,
      ctx,
      w: window.innerWidth,
      h: window.innerHeight,
      nt: 0
    };
    
    ctx.canvas.width = window.innerWidth;
    ctx.canvas.height = window.innerHeight;
    ctx.filter = `blur(${blur}px)`;

    window.onresize = function () {
      ctx.canvas.width = window.innerWidth;
      ctx.canvas.height = window.innerHeight;
      ctx.filter = `blur(${blur}px)`;
      contextRef.current.w = window.innerWidth;
      contextRef.current.h = window.innerHeight;
    };
    
    render();
  }, [blur, render]);

  useEffect(() => {
    init();
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [init]);

  const [isSafari, setIsSafari] = useState(false);
  useEffect(() => {
    setIsSafari(
      typeof window !== "undefined" &&
      navigator.userAgent.includes("Safari") &&
      !navigator.userAgent.includes("Chrome")
    );
  }, []);

  return (
    <div className={cn("h-screen flex flex-col items-center justify-center", containerClassName)}>
      <canvas
        className="absolute inset-0 z-0"
        ref={canvasRef}
        id="canvas"
        style={{
          ...(isSafari ? { filter: `blur(${blur}px)` } : {}),
        }}
      />
      <div className={cn("relative z-10", className)} {...props}>
        {children}
      </div>
    </div>
  );
};
